import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, BookOpenText, Buildings } from "@phosphor-icons/react";
import SortSelect, { CATALOG_SORT_OPTIONS } from "@/components/SortSelect";
import Seo from "@/components/Seo";

const ALL_PUBLISHERS = "__all__";

export default function Database() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const initialPub = searchParams.get("publisher") || ALL_PUBLISHERS;
  const [items, setItems] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [publisher, setPublisher] = useState(initialPub);
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/publishers").then(r => setPublishers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, sort, publisher]);

  async function load() {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (publisher && publisher !== ALL_PUBLISHERS) params.publisher = publisher;
    const { data } = await api.get("/manga", { params });
    setItems(data);
    setLoading(false);
    const urlParams = {};
    if (q) urlParams.q = q;
    if (publisher && publisher !== ALL_PUBLISHERS) urlParams.publisher = publisher;
    setSearchParams(urlParams, { replace: true });
  }

  return (
    <div className="pt-20 max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="database-page">
      <Seo title="Catálogo de Mangás" description="Todos os mangás disponíveis em Portugal num só sítio. Pesquisa por título, autor, editora ou ISBN." path="/database" />
      <div className="mb-8">
        <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ BASE DE DADOS</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Catálogo de Mangás</h1>
        <p className="text-zinc-400 mt-2 max-w-xl">Todos os mangás disponíveis em Portugal num só sítio.</p>
      </div>

      <div className="mb-8 sticky top-16 bg-[#09090B] py-4 z-30">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Pesquisar título, autor, editora ou ISBN..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 bg-[#121217] border-white/10 h-11"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Buildings size={14} weight="bold" className="text-zinc-500" />
            <span className="text-[10px] font-mono-accent text-zinc-500 hidden sm:inline">EDITORA</span>
            <Select value={publisher} onValueChange={setPublisher}>
              <SelectTrigger className="w-[200px] h-9 bg-[#121217] border-white/10 text-sm" data-testid="database-publisher-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121217] border-white/10 text-white max-h-72">
                <SelectItem value={ALL_PUBLISHERS} data-testid="database-publisher-opt-all">Todas as editoras</SelectItem>
                {publishers.map(p => (
                  <SelectItem key={p.publisher_id} value={p.name} data-testid={`database-publisher-opt-${p.publisher_id}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <SortSelect value={sort} onChange={setSort} options={CATALOG_SORT_OPTIONS} testid="database-sort" />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-20 font-mono-accent text-xs">A CARREGAR...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <BookOpenText size={42} weight="duotone" className="text-[#7209B7] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem resultados</div>
          <div className="text-sm text-zinc-500 mt-1">Tenta outra pesquisa.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {items.map((m) => (
            <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="group" data-testid={`manga-card-${m.manga_id}`}>
              <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#121217] border border-white/5 relative">
                {m.cover_url ? (
                  <img src={m.cover_url} alt={m.title} className="manga-cover w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-700 font-mono-accent text-xs">SEM CAPA</div>
                )}
              </div>
              <div className="mt-3">
                <div className="font-display font-semibold leading-tight line-clamp-2 group-hover:text-[#E63946] transition-colors">{m.title}</div>
                <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">{m.author || "—"}</div>
                {m.isbn && <div className="font-mono-accent text-[10px] text-zinc-600 mt-0.5">ISBN: {m.isbn}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
